import { useState, useMemo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';
import {
  Plus,
  Search,
  MoreHorizontal,
  Star,
  Pencil,
  Archive,
  Trash2,
  ChevronDown,
  Users,
  CheckCheck,
} from 'lucide-react';
import { apiPost } from '@/lib/api';
import { showToast } from '@/components/Toast';
import { useProjects } from '@/hooks/project/useProjects';
import { useProjectsStore } from '@/stores';
import MobileHeader from '@/components/MobileHeader';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ConfirmDialog from '@/components/ConfirmDialog';

// Force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => i18nInstance.off('languageChanged', onLanguageChanged);
  }, []);
};

interface ProjectsListProps {
  onProjectSelect: (projectId: string) => void;
  onNewProject: () => void;
  onStarProject?: (projectId: string) => void;
  onEditProject?: (projectId: string) => void; // Takes project ID, fetches project from store
  onManageMembers?: (projectId: string, projectName: string) => void;
  onArchiveProject?: (
    projectId: string,
    archived: boolean
  ) => void | Promise<void>;
  onDeleteProject?: (projectId: string, role: string | undefined) => void;
  initialTab?: 'active' | 'archived';
}

export default function ProjectsList({
  onProjectSelect,
  onNewProject,
  onStarProject,
  onEditProject,
  onManageMembers,
  onArchiveProject,
  onDeleteProject,
  initialTab = 'active',
}: ProjectsListProps) {
  const { t } = useTranslation();
  useForceUpdate();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'activity' | 'edited' | 'created'>(
    'activity'
  );
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>(initialTab);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    projectId?: string;
    projectName?: string;
    projectRole?: string;
  }>({ isOpen: false });
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const projects = useProjectsStore(state => state.projects);
  const { loadProjects } = useProjects();

  // Sync activeTab when initialTab changes from outside
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // 🔥 每次进入 #/projects 路由时刷新项目列表
  // 确保退出的项目不再显示，避免点击报错
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Separate active and archived projects
  const { activeProjects, archivedProjects } = useMemo(() => {
    return {
      activeProjects: projects.filter(p => !p.archived),
      archivedProjects: projects.filter(p => p.archived),
    };
  }, [projects]);

  // Auto-switch to "Your projects" tab when archived projects list becomes empty
  // This happens after unarchiving the last project in the Archived tab
  if (activeTab === 'archived' && archivedProjects.length === 0) {
    setActiveTab('active');
  }

  // Select projects based on active tab
  const tabProjects =
    activeTab === 'active' ? activeProjects : archivedProjects;

  const filteredProjects = useMemo(() => {
    let filtered = tabProjects;

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(project =>
        project.name.toLowerCase().includes(searchQuery.trim().toLowerCase())
      );
    }

    // Sort projects
    const sorted = [...filtered].sort((a, b) => {
      const aDate = new Date(
        (a as any)[
          sortBy === 'activity'
            ? 'lastActiveAt'
            : sortBy === 'edited'
              ? 'updatedAt'
              : 'createdAt'
        ] || 0
      );
      const bDate = new Date(
        (b as any)[
          sortBy === 'activity'
            ? 'lastActiveAt'
            : sortBy === 'edited'
              ? 'updatedAt'
              : 'createdAt'
        ] || 0
      );
      return bDate.getTime() - aDate.getTime(); // Newest first
    });

    return sorted;
  }, [tabProjects, searchQuery, sortBy]);

  // Calculate unvisited projects count
  const unvisitedCount = useMemo(() => {
    return projects.filter(p => !p.archived && !p.lastVisitedAt).length;
  }, [projects]);

  // Mark all as read handler
  const handleMarkAllRead = useCallback(async () => {
    if (unvisitedCount === 0) return;
    setIsMarkingAllRead(true);
    try {
      await apiPost('/api/projects/visit', {});
      // Refresh projects list to update lastVisitedAt
      await loadProjects();
      showToast(t('toast.allMarkedAsRead'), 'success');
    } catch (err) {
      showToast(t('toast.failedToMarkAllAsRead'), 'error');
    } finally {
      setIsMarkingAllRead(false);
    }
  }, [unvisitedCount, loadProjects, t]);

  const formatDate = (dateString: string | Date | undefined) => {
    if (!dateString) return t('common.recently');

    // Parse UTC time and convert to local time
    const date = new Date(dateString);
    const now = new Date();

    // Get local date strings for comparison (YYYY-MM-DD format)
    const dateLocal = new Date(date.getTime()).toLocaleDateString('en-CA'); // YYYY-MM-DD
    const nowLocal = new Date(now.getTime()).toLocaleDateString('en-CA');

    // Calculate yesterday's date
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayLocal = yesterday.toLocaleDateString('en-CA');

    // Check if same day (today)
    if (dateLocal === nowLocal) return t('project.updatedToday');

    // Check if yesterday
    if (dateLocal === yesterdayLocal) return t('project.updatedYesterday');

    // Calculate difference in days
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 7) return t('project.updatedDaysAgo', { count: diffDays });
    if (diffDays < 30)
      return t('project.updatedWeeksAgo', { count: Math.floor(diffDays / 7) });
    return t('project.updatedMonthsAgo', { count: Math.floor(diffDays / 30) });
  };

  return (
    <div className="flex-1 flex flex-col h-full">
      {/* Mobile Header */}
      <MobileHeader>
        <h3 className="text-base font-bold">{t('sidebar.projects')}</h3>
      </MobileHeader>
      {/* Header with max-width container */}
      <div className="flex-shrink-0 w-full">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 pt-6 sm:pt-12 pb-6">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-normal text-gray-900 font-serif">
              {t('sidebar.projects')}
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={handleMarkAllRead}
                disabled={unvisitedCount === 0 || isMarkingAllRead}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors ${
                  unvisitedCount === 0 || isMarkingAllRead
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
                title={
                  unvisitedCount === 0
                    ? t('tooltips.noUnreadItems')
                    : t('tooltips.markAsRead', { count: unvisitedCount })
                }
              >
                <CheckCheck size={16} />
                <span>
                  {isMarkingAllRead
                    ? t('common.marking')
                    : t('tooltips.markAllAsRead')}
                </span>
              </button>
              <button
                onClick={onNewProject}
                className="flex items-center gap-2 px-2 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
              >
                <Plus size={16} className="flex-shrink-0" />
                <span className="hidden sm:inline">
                  {t('project.createProject')}
                </span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder={t('form.searchProjects')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-200 bg-white text-sm"
            />
          </div>

          {/* Tabs and Sort by */}
          {(activeProjects.length > 0 || archivedProjects.length > 0) && (
            <div className="flex flex-row items-center justify-between gap-2 sm:gap-3 mb-6">
              {/* Tabs on the left */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveTab('active')}
                  className={`px-2 py-2 text-sm font-medium rounded-xl transition-colors lg:px-4 ${
                    activeTab === 'active'
                      ? 'text-[#141413]'
                      : 'bg-transparent text-gray-700 hover:text-gray-900'
                  }`}
                  style={
                    activeTab === 'active' ? { backgroundColor: '#e8e6dc' } : {}
                  }
                >
                  {t('project.yourProjects')}
                </button>
                {archivedProjects.length > 0 && (
                  <button
                    onClick={() => setActiveTab('archived')}
                    className={`px-2 py-2 text-sm font-medium rounded-xl transition-colors lg:px-4 ${
                      activeTab === 'archived'
                        ? 'text-[#141413]'
                        : 'bg-transparent text-gray-700 hover:text-gray-900'
                    }`}
                    style={
                      activeTab === 'archived'
                        ? { backgroundColor: '#e8e6dc' }
                        : {}
                    }
                  >
                    {t('project.archived')}
                  </button>
                )}
              </div>

              {/* Sort by on the right */}
              <div className="flex items-center gap-2 text-sm sm:justify-end">
                <span className="text-gray-500 hidden lg:inline-block">
                  {t('project.sortBy')}
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1 px-2 py-1 border border-gray-200 rounded-md hover:bg-[#f3f3f0] focus:outline-none focus:ring-2 focus:ring-gray-200 bg-white text-gray-700">
                      <span>
                        {sortBy === 'activity'
                          ? t('project.sortRecentActivity')
                          : sortBy === 'edited'
                            ? t('project.sortLastEdited')
                            : t('project.sortDateCreated')}
                      </span>
                      <ChevronDown size={14} className="text-gray-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem
                      onClick={() => setSortBy('activity')}
                      className="cursor-pointer hover:bg-[#f3f3f0] focus:bg-[#f3f3f0]"
                    >
                      {t('project.sortRecentActivity')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setSortBy('edited')}
                      className="cursor-pointer hover:bg-[#f3f3f0] focus:bg-[#f3f3f0]"
                    >
                      {t('project.sortLastEdited')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setSortBy('created')}
                      className="cursor-pointer hover:bg-[#f3f3f0] focus:bg-[#f3f3f0]"
                    >
                      {t('project.sortDateCreated')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Projects Grid with max-width container */}
      <div className="flex-1 overflow-y-auto w-full">
        <div className="max-w-5xl mx-auto px-8 pb-12">
          {filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <p className="text-lg mb-2">{t('project.noProjectsFound')}</p>
              <p className="text-sm">
                {searchQuery
                  ? t('project.tryDifferentSearch')
                  : t('project.createNewProjectToStart')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProjects.map(project => (
                <div
                  key={project.id}
                  className={`relative group p-5 border border-gray-300 rounded-xl transition-all flex flex-col hover:bg-white cursor-pointer`}
                >
                  <div
                    onClick={() => onProjectSelect(project.id)}
                    className="flex-1 flex flex-col min-h-0"
                  >
                    {/* Header with title and star */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
                        {!project.lastVisitedAt && (
                          <span
                            className="flex-shrink-0 w-2 h-2 bg-red-500 rounded-full"
                            title={t('common.new')}
                          />
                        )}
                        <h3 className="font-bold text-gray-900 truncate text-sm">
                          {project.name}
                        </h3>
                        {project.starred && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              onStarProject?.(project.id);
                            }}
                            className="flex-shrink-0"
                          >
                            <Star
                              size={14}
                              className="fill-yellow-500 text-yellow-500"
                            />
                          </button>
                        )}
                      </div>

                      {/* Three dots menu - visible on hover */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            onClick={e => e.stopPropagation()}
                            disabled={project.archived}
                            className={`p-1 hover:bg-gray-100 rounded-md text-gray-400 hover:text-gray-600 border border-transparent hover:border-gray-200 transition-all cursor-pointer`}
                          >
                            <MoreHorizontal size={16} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {!project.archived && (
                            <>
                              <DropdownMenuItem
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onStarProject?.(project.id);
                                }}
                              >
                                <Star
                                  size={14}
                                  className="mr-2 text-yellow-500"
                                />
                                {project.starred
                                  ? t('chat.unstar')
                                  : t('chat.star')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onEditProject?.(project.id);
                                }}
                                disabled={
                                  project.role === 'viewer' ||
                                  project.role === 'editor'
                                }
                                className={
                                  project.role === 'viewer' ||
                                  project.role === 'editor'
                                    ? 'cursor-not-allowed opacity-50'
                                    : ''
                                }
                              >
                                <Pencil size={14} className="mr-2" />
                                {t('project.editDetails')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onManageMembers?.(project.id, project.name);
                                }}
                              >
                                <Users size={14} className="mr-2" />
                                {t('common.members')}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onArchiveProject?.(
                                    project.id,
                                    !project.archived
                                  );
                                }}
                                disabled={
                                  project.role === 'viewer' ||
                                  project.role === 'editor'
                                }
                                className={
                                  project.role === 'viewer' ||
                                  project.role === 'editor'
                                    ? 'cursor-not-allowed opacity-50'
                                    : ''
                                }
                              >
                                <Archive size={14} className="mr-2" />
                                {t('sidebar.archived')}
                              </DropdownMenuItem>
                              {/* 🔥 只有 owner 可以删除项目，manager/editor/viewer 都不能删除 */}
                              <DropdownMenuItem
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  setConfirmDialog({
                                    isOpen: true,
                                    projectId: project.id,
                                    projectName: project.name,
                                    projectRole: project.role,
                                  });
                                }}
                                disabled={project.role !== 'owner'}
                                className={`text-red-600 focus:text-red-600 ${project.role !== 'owner' ? 'cursor-not-allowed opacity-50' : ''}`}
                              >
                                <Trash2 size={14} className="mr-2" />
                                {t('common.delete')}
                              </DropdownMenuItem>
                            </>
                          )}
                          {/* 🔥 Members for archived projects - 所有角色都可以访问查看 */}
                          {project.archived && (
                            <DropdownMenuItem
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onManageMembers?.(project.id, project.name);
                              }}
                            >
                              <Users size={14} className="mr-2" />
                              {t('common.members')}
                            </DropdownMenuItem>
                          )}
                          {/* 🔥 只有 owner 和 manager 可以 Unarchive */}
                          {project.archived &&
                            (project.role === 'owner' ||
                              project.role === 'manager') && (
                              <DropdownMenuItem
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  onArchiveProject?.(
                                    project.id,
                                    !project.archived
                                  );
                                }}
                              >
                                <Archive size={14} className="mr-2" />
                                {t('project.unarchive')}
                              </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-700 mb-auto line-clamp-3 leading-relaxed">
                      {project.description || ''}
                    </p>

                    {/* Bottom row: Role on left, Updated time on right */}
                    <div className="flex items-center mt-4">
                      {project.role && (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                            project.role === 'owner'
                              ? 'bg-purple-100 text-purple-700 border border-purple-700'
                              : project.role === 'manager'
                                ? 'bg-blue-100 text-blue-700 border border-blue-700'
                                : project.role === 'editor'
                                  ? 'bg-green-100 text-green-700 border border-green-700'
                                  : 'bg-gray-100 text-gray-600 border border-gray-600'
                          }`}
                        >
                          {t(`roles.${project.role}`)}
                        </span>
                      )}
                      <div className="text-xs text-gray-400 ml-auto">
                        {formatDate(project.lastActiveAt || project.updatedAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Project Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={t('confirmDialog.deleteProject')}
        message={t('confirmDialog.deleteProjectConfirm', {
          name: confirmDialog.projectName,
        })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        onConfirm={() => {
          if (confirmDialog.projectId) {
            onDeleteProject?.(
              confirmDialog.projectId,
              confirmDialog.projectRole
            );
          }
          setConfirmDialog({ isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ isOpen: false })}
      />
    </div>
  );
}
